// #474 — the order ticket's result line: one typed model, one producer per
// venue path, rendered by OrderResultBanner.
//
// The ticket has a single result slot shared by every path (plain Kalshi and
// Polymarket placements, Polymarket typed order-attempt outcomes, and Oscar
// sequences), so the line contract lives here beside the Oscar producer. A
// plain placement sets `{ ok, msg }` and gets green/red from `ok`. A producer
// that has authoritative venue evidence adds a severity on top: an Oscar
// sequence from the relay's per-leg result (below), and a typed Polymarket
// order attempt from its submission-phase code (#556 —
// `polyOrderResultPresentation`, kept in its own module so the relay client
// stays out of the banner's import graph).
//
// This module is PASSIVE and derives nothing about the venue. Every field it
// reads is authoritative relay output (`leg.ok`, `leg.skipped`, `leg.code`,
// `leg.filledCount`, `leg.count`). When the relay does not classify who owns a
// blocker, the wording stays generic — the frontend never infers ownership,
// trader identity, or sequence completion from local state (#468, #472).

import type { OscarSequenceResult } from '../../types/kalshi';

/** How loudly the result line reads — and, on the banner, whether a screen
 *  reader is interrupted. `success` and `pending` are announced politely.
 *
 *  `pending` is the in-flight rung (`orderAttemptIdentity`): the request has
 *  been dispatched and the venue has not answered. It is neutral by design —
 *  neither a claim of acceptance nor a warning — and it is the only rung a
 *  line may carry before venue evidence exists. */
export type OrderResultSeverity = 'success' | 'warning' | 'critical' | 'pending';

/**
 * The wording classes the Oscar result line distinguishes. Each maps to one
 * fixed message shape, so a test (or a reader) can name an outcome without
 * matching on prose.
 */
export type OscarResultKind =
  /** The sequence did what it was asked to do. */
  | 'success'
  /** An existing entity order refused the overtake before any contract filled. */
  | 'entity_blocker_zero_fill'
  /** Some contracts filled, then the existing blocker stopped the overtake. */
  | 'entity_blocker_partial_fill'
  /** The old blocker is gone and the take did NOT land: the level is exposed. */
  | 'naked_level'
  /** The take stands but its blocker did not: the position rests unprotected. */
  | 'blocker_failed'
  /** The take failed with nothing placed and nothing released. */
  | 'take_failed'
  /** A release never got past the cancel, so nothing else fired. */
  | 'release_aborted'
  /** The sequence landed, but one or more registry kill legs failed — the
   *  take's maker STP cleared the path at the venue instead. */
  | 'kill_failed_backstopped';

export interface OrderResultLine {
  /** Preserved from the pre-#474 contract; the fallback tone for plain results. */
  ok: boolean;
  msg: string;
  /** Absent unless a producer has venue evidence for the tone (an Oscar
   *  sequence, or a typed Polymarket outcome code). When absent — the common
   *  case for a plain placement — the banner derives the tone from `ok`. */
  severity?: OrderResultSeverity;
  kind?: OscarResultKind;
  /** Take-leg fill numbers exactly as the relay reported them, null when it
   *  did not say. Never reconstructed from the message text. */
  fill?: { filledCount: number; requestedCount: number } | null;
  /** Hover reference beside the line (the banner's `title`). The line itself
   *  carries the action-critical state; this is never its only copy. */
  reference?: string;
}

/**
 * The tone a line renders with. Plain placements carry no severity and keep
 * the pre-#474 two-tone behavior — deliberately red rather than the amber a
 * nothing-placed failure earns, because a bare `{ ok: false }` does not say
 * whether an order reached the venue.
 *
 * A producer may only set a severity when its venue evidence settles that
 * question: the relay's per-leg sequence result does (#474), and so does a
 * typed Polymarket outcome code, which the relay emits from the observed
 * submission phase (#556). Everything else keeps the red fallback.
 */
export function resultSeverity(line: OrderResultLine): OrderResultSeverity {
  return line.severity ?? (line.ok ? 'success' : 'critical');
}

/**
 * Ordered by EXPOSURE, not by how much of the sequence ran.
 *
 * critical = the trader is holding contracts with nothing protecting them and
 * has to act. warning = the attempt failed but the book is untouched, so the
 * only cost is a retry.
 *
 * This deliberately differs from the durable receipt's scale, which tones a
 * `partial` sequence amber: the receipt answers "how much of the sequence
 * executed", the banner answers "what are you holding right now". A partial
 * fill is a mild audit outcome and an unprotected position at the same time.
 */
const SEVERITY_BY_KIND: Record<OscarResultKind, OrderResultSeverity> = {
  success: 'success',
  naked_level: 'critical',
  blocker_failed: 'critical',
  entity_blocker_partial_fill: 'critical',
  entity_blocker_zero_fill: 'warning',
  take_failed: 'warning',
  release_aborted: 'warning',
  kill_failed_backstopped: 'warning',
};

const legLabel = (side: string | undefined, priceCents: number | undefined) =>
  `${priceCents ?? '?'}¢ ${(side ?? '').toUpperCase()}`;

const line = (
  kind: OscarResultKind,
  msg: string,
  fill: OrderResultLine['fill'] = null,
): OrderResultLine => ({
  ok: kind === 'success',
  severity: SEVERITY_BY_KIND[kind],
  kind,
  msg,
  fill,
});

/**
 * Render a sequence result into the ticket's one-line result message. The
 * loud cases are the asymmetric ones — a take standing without its blocker,
 * or (worst) a released blocker with a failed take (naked level: the ticket
 * stays armed so re-fire is one click; see the caller).
 */
export function describeOscarSequenceResult(result: OscarSequenceResult): OrderResultLine {
  const take = result.legs.find((l) => l.role === 'take');
  const blocker = result.legs.find((l) => l.role === 'blocker');
  const cancel = result.legs.find((l) => l.role === 'cancel');
  const kills = result.legs.filter((l) => l.role === 'kill');

  // Only the take leg reports fills; a null from the venue stays null.
  const fill =
    take?.filledCount != null && take.count != null
      ? { filledCount: take.filledCount, requestedCount: take.count }
      : null;

  if (cancel && !cancel.ok) {
    if (cancel.skipped) {
      return line('release_aborted', 'Sequence aborted before the blocker release.', fill);
    }
    return line(
      'release_aborted',
      `Release aborted — blocker cancel failed: ${cancel.error ?? 'unknown'}. Blocker still resting; nothing else fired.`,
      fill,
    );
  }

  if (take && !take.ok) {
    const retry = take.code === 'price_moved' ? ' Price is still loaded — try again.' : '';
    if (take.code === 'entity_order_blocker') {
      const partial = fill != null && fill.filledCount > 0;
      const filled = partial
        ? `${fill.filledCount} of ${fill.requestedCount} contracts filled before the existing blocker stopped the overtake. No new blocker was placed.`
        : 'Overtake failed: existing blocker. No new order was placed.';
      if (cancel?.ok) {
        return line(
          'naked_level',
          `⚠ BLOCKER RELEASED. ${filled} Level is naked; resolve the existing blocker before retrying.`,
          fill,
        );
      }
      return line(
        partial ? 'entity_blocker_partial_fill' : 'entity_blocker_zero_fill',
        filled,
        fill,
      );
    }
    if (cancel?.ok) {
      return line(
        'naked_level',
        `⚠ BLOCKER RELEASED but take FAILED — level is naked. ${take.error ?? 'unknown'}.${retry} Re-fire immediately.`,
        fill,
      );
    }
    return line(
      'take_failed',
      `Overtake failed — nothing placed: ${take.error ?? 'unknown'}.${retry}`,
      fill,
    );
  }

  const takeDesc = take
    ? `took ${legLabel(take.side, take.priceCents)} ×${take.count ?? '?'}` +
      (take.filledCount != null ? ` (${take.filledCount} filled)` : '')
    : '';

  if (blocker && !blocker.ok) {
    return line(
      'blocker_failed',
      `${takeDesc ? takeDesc[0].toUpperCase() + takeDesc.slice(1) : 'Take landed'} — BLOCKER FAILED (${blocker.error ?? 'unknown'}). Position resting unprotected.`,
      fill,
    );
  }

  const parts: string[] = [];
  if (cancel?.ok) parts.push(cancel.alreadyGone ? 'blocker already gone' : 'blocker released');
  // Kill-leg summary (#472). Ownership wording is derived only from the
  // relay's ownBlocker flag — "your"/"a teammate's", never a locally-inferred
  // identity. The pre-fire preview and owner notice use the same privacy-safe
  // self/teammate classes supplied by the relay.
  const killedOk = kills.filter((l) => l.ok);
  if (killedOk.length === 1) {
    parts.push(
      `killed ${killedOk[0].ownBlocker ? 'your' : "a teammate's"} blocker @ ${killedOk[0].blockerPriceCents ?? '?'}¢`,
    );
  } else if (killedOk.length > 1) {
    parts.push(`killed ${killedOk.length} blockers in the path`);
  }
  if (takeDesc) parts.push(takeDesc);
  if (blocker?.ok) {
    parts.push(`blocker ${legLabel(blocker.side, blocker.priceCents)} ×${blocker.count ?? '?'} resting`);
  }
  const joined = parts.length ? parts.join(' · ') : 'Sequence complete';
  const msg = joined[0].toUpperCase() + joined.slice(1);
  // The relay is the authority on completion. A sequence it refused to call ok
  // stays not-ok even when every leg inspected above looks clean — and it gets
  // NO wording class, because none of the classes above describes it.
  if (!result.ok) return { ok: false, msg, fill };
  // A failed kill on an otherwise-ok sequence downgrades to a warning: the
  // maker-STP take cleared the path at the venue, but the targeted cancel
  // (and its registry/owner bookkeeping) did not land.
  const failedKills = kills.filter((l) => !l.ok && !l.skipped).length;
  if (failedKills > 0) {
    // ok stays TRUE — the sequence landed; only the bookkeeping cancel missed.
    return {
      ...line(
        'kill_failed_backstopped',
        `${msg} — ⚠ ${failedKills} blocker kill${failedKills > 1 ? 's' : ''} failed; the take's maker STP cleared the path at the venue.`,
        fill,
      ),
      ok: true,
    };
  }
  return line('success', msg, fill);
}
